import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiClient } from '../api/client'
import { getTelegramUserId } from '../utils/telegram'
import { useLanguage } from '../hooks/useLanguage'

interface SpendRecord {
  id: string
  action: string
  target_id?: string
  amount: number
  description: string
  created_at: string
  status: string
}

interface SpendingResponse {
  spent: number
  currency: string
  spend_history: SpendRecord[]
}

const rules = [
  ['帮我关注', 'Follow', '1 credit'],
  ['展开说说', 'Explain', '1 credit'],
  ['政策解读', 'Policy analysis', '1 credit / event'],
  ['继续追问', 'Ask follow-up', '1 credit / time'],
  ['查看详情', 'View details', '1 credit'],
  ['详情下一页', 'Next detail', '1 credit'],
  ['查看时间轴', 'View timeline', '1 credit'],
]

export function SpendingPage() {
  const navigate = useNavigate()
  const { t } = useLanguage()
  const [data, setData] = useState<SpendingResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    apiClient.get<SpendingResponse>('/api/user/spending', {
      chat_id: getTelegramUserId(),
    })
      .then(setData)
      .catch((error) => {
        console.error('Failed to load spending:', error)
      })
      .finally(() => setIsLoading(false))
  }, [])

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      <header className="sticky top-0 z-20 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="w-8 h-8 rounded-md bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200"
            aria-label={t('返回', 'Back')}
          >
            ←
          </button>
          <div>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">{t('消耗记录', 'Spending Records')}</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t(
                `已消耗 ${isLoading ? '--' : data?.spent ?? 0} ${data?.currency || 'credits'}`,
                `Spent ${isLoading ? '--' : data?.spent ?? 0} ${data?.currency || 'credits'}`
              )}
            </p>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-4 space-y-4">
        <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('扣费规则', 'Pricing Rules')}</h2>
          <div className="space-y-2">
            {rules.map(([name, nameEn, cost]) => (
              <div key={name} className="flex items-center justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-300">{t(name, nameEn)}</span>
                <span className="font-medium text-gray-900 dark:text-white">{cost}</span>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-3">{t('历史消耗', 'Spending History')}</h2>

          {!isLoading && (!data?.spend_history || data.spend_history.length === 0) && (
            <div className="text-sm text-gray-500 dark:text-gray-400">{t('暂无消耗记录', 'No spending records')}</div>
          )}

          <div className="space-y-2">
            {data?.spend_history.map((record) => (
              <div
                key={record.id}
                className="flex items-center justify-between py-2 border-t border-gray-100 dark:border-gray-700 first:border-t-0"
              >
                <div className="min-w-0">
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {record.description || record.action}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">
                    {new Date(record.created_at).toLocaleString()}
                  </div>
                </div>
                <span className="text-sm font-semibold text-red-600 dark:text-red-300">
                  -{record.amount}
                </span>
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  )
}
